import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay, catchError, switchMap, throwError } from 'rxjs';
import { IUserSettingsService, UserSettings } from '@infinitform/platform-core';
import { CLOUD_PLATFORM_CONFIG } from '../config';

@Injectable()
export class UserSettingsCloudService implements IUserSettingsService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CLOUD_PLATFORM_CONFIG);

  private get baseUrl(): string {
    return `${this.config.apiBaseUrl}/user/settings`;
  }

  /**
   * One in-flight/last response shared by every caller.
   *
   * Opening the editor used to fetch this endpoint once per consumer -- theme,
   * accessibility, presets, beta features -- for the same document. They now
   * join the first request instead.
   */
  private cached?: Observable<UserSettings>;

  get(): Observable<UserSettings> {
    return (this.cached ??= this.http.get<UserSettings>(this.baseUrl).pipe(
      // Without this an error would be replayed to every later caller, so a
      // single failed fetch would look permanent.
      catchError((error) => {
        this.cached = undefined;
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    ));
  }

  invalidate(): void {
    this.cached = undefined;
  }

  update(settings: Partial<UserSettings>): Observable<UserSettings> {
    return this.http.post<unknown>(this.baseUrl, settings).pipe(switchMap((response) => this.cacheSaved(response)));
  }

  reset(): Observable<UserSettings> {
    return this.http.post<unknown>(this.baseUrl, {
      mouse: {
        left: 'select',
        middle: 'pan',
        right: 'rotate',
        doubleMiddle: 'fitAll',
      },
      graphics: {
        quality: 'medium',
        antialiasing: true,
        enhancedUi: true,
      },
      theme: {
        theme: 'light',
      },
      accessibility: {
        colorBlindMode: 'off',
      },
      defaultUnitSystem: 'metric',
    }).pipe(switchMap((response) => this.cacheSaved(response)));
  }

  /**
   * Keeps a save answer as the shared document only when it is one; older
   * servers answer with a status object, so the document is read again.
   */
  private cacheSaved(response: unknown): Observable<UserSettings> {
    if (isSettingsDocument(response)) {
      this.cached = of(response);
      return of(response);
    }
    this.cached = undefined;
    return this.get();
  }
}

function isSettingsDocument(value: unknown): value is UserSettings {
  return typeof value === 'object' && value !== null && 'mouse' in value && 'graphics' in value;
}
